
import React from 'react';

const GameStrategy = () => {
  const strategyTips = [
    {
      phase: "Early Game (First Hour)",
      tips: [
        "Focus entirely on click power upgrades",
        "Save for your first auto-clicker after reaching 5x click power",
        "Complete easy achievements for bonus PokeDollars",
        "Evolve your starter Pokemon as soon as possible"
      ]
    },
    {
      phase: "Mid Game (Hours 2-5)",
      tips: [
        "Balance investments between click power and auto-clickers",
        "Start focusing on type-specific bonuses",
        "Challenge the first few gym leaders",
        "Begin working toward special evolutions"
      ]
    },
    {
      phase: "Late Game (Hours 5+)",
      tips: [
        "Invest heavily in auto-generation",
        "Focus on completing the Pokedex",
        "Save PokeGems for legendary Pokemon encounters",
        "Prepare for prestige system"
      ]
    }
  ];

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-lg shadow-xl overflow-hidden">
        <div className="p-6 sm:p-10">
          <h2 className="text-3xl font-bold text-center text-white mb-8">
            Advanced Strategy Guide for Pokemon Clicker 2
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Progression Strategies by Game Phase</h3>
            
            <div className="space-y-8 mb-10">
              {strategyTips.map((strategy, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4">
                  <h4 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{strategy.phase}</h4>
                  <ul className="space-y-2">
                    {strategy.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start">
                        <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-300">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Economic Strategy</h3>
            
            <div className="prose prose-lg max-w-none dark:prose-invert mb-8">
              <p>
                Efficient resource management is crucial in Pokemon Clicker 2. Your two primary currencies—PokeDollars and PokeGems—need to be allocated strategically to maximize your progress.
              </p>
              
              <h4>PokeDollars Management</h4>
              <p>
                PokeDollars are your main currency, earned through clicks and auto-generation. The optimal spending strategy evolves as you progress:
              </p>
              <ul>
                <li><strong>Early game:</strong> Invest in click power at a ratio of approximately 70% click power upgrades to 30% auto-clickers.</li>
                <li><strong>Mid game:</strong> Shift to a 40% click power, 60% auto-clicker distribution as passive generation becomes more important.</li>
                <li><strong>Late game:</strong> Focus 80% on auto-clickers and special upgrades, with only 20% going to click power.</li>
              </ul>
              
              <h4>PokeGems Strategy</h4>
              <p>
                PokeGems are a premium currency that should be spent only on high-value permanent upgrades:
              </p>
              <ul>
                <li>Save PokeGems exclusively for legendary Pokemon encounters and permanent multipliers.</li>
                <li>Avoid spending PokeGems on temporary boosts unless you're attempting to reach a specific milestone.</li>
                <li>Complete achievements systematically to maintain a steady income of PokeGems.</li>
              </ul>
            </div>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Team Building Strategy</h3>
            
            <div className="mb-8">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Building an effective team of Pokemon is essential for maximizing your clicking efficiency and bonuses. Different Pokemon types offer different advantages:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-red-100 dark:bg-red-900 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Fire Types</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Specialize in critical hit bonuses. Include Pokemon like Charizard and Arcanine to increase your chances of landing powerful critical clicks.
                  </p>
                </div>
                
                <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Water Types</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Excel at auto-clicking efficiency. Pokemon like Blastoise and Gyarados boost your passive income generation significantly.
                  </p>
                </div>
                
                <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Electric Types</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Provide speed bonuses. Pikachu and Raichu increase the overall speed of gameplay, including evolution rates and shop refresh times.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-100 dark:bg-green-900 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Grass Types</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Focus on resource generation. Venusaur and other grass types provide bonuses to resource drops and special item discoveries.
                  </p>
                </div>
                
                <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Psychic Types</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Enhance experience gain. Alakazam and similar psychic Pokemon increase the effectiveness of experience point acquisition.
                  </p>
                </div>
                
                <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Normal Types</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Provide balanced bonuses. Pokemon like Snorlax offer well-rounded benefits to all aspects of gameplay.
                  </p>
                </div>
              </div>
            </div>
            
            <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Prestige System Strategy</h3>
            
            <div className="prose prose-lg max-w-none dark:prose-invert mb-8">
              <p>
                The prestige system becomes available after reaching specific milestones in Pokemon Clicker 2. This system allows you to reset your progress in exchange for permanent bonuses:
              </p>
              
              <h4>When to Prestige</h4>
              <p>
                The optimal time to prestige follows a diminishing returns curve:
              </p>
              <ul>
                <li><strong>First prestige:</strong> Around 1,000,000 total clicks or when progress significantly slows.</li>
                <li><strong>Second prestige:</strong> At approximately 5,000,000 total clicks with your bonuses.</li>
                <li><strong>Subsequent prestiges:</strong> When acquiring new upgrades takes more than a few hours of active play.</li>
              </ul>
              
              <h4>What Carries Over</h4>
              <p>
                Understanding what persists through prestige is crucial for planning:
              </p>
              <ul>
                <li>Pokedex entries remain unlocked</li>
                <li>Achievement progress is maintained</li>
                <li>PokeGems and purchases made with them persist</li>
                <li>Prestige multipliers accumulate with each reset</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900 dark:to-orange-900 p-6 rounded-lg shadow-inner">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-yellow-100">Expert Secret Technique: The "Magikarp Strategy"</h3>
              <p className="text-gray-800 dark:text-yellow-200 mb-4">
                Advanced players have discovered that focusing early resources on evolving Magikarp into Gyarados—despite its high click requirement—provides an exponential return on investment that significantly outpaces other strategies in the long run.
              </p>
              <div className="flex items-center">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <p className="text-gray-800 dark:text-yellow-200 font-medium">
                  This strategy can yield a 500% faster progression rate by mid-game when executed correctly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameStrategy;
